import type { ITeacherClass } from '@/types/teacher';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';

import { IconSearch } from '@tabler/icons-react';

import ClassSummaryCard from '@/components/ClassSummaryCard';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Pagination from '@/components/Pagination';
import AssignedClassesSkeleton from '@/components/SkeletonLoader/AssignedClassesSkeleton';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getSeeMoreClasses } from '@/service/teacherService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';

const AssignedClasses = () => {
  const { formatText } = useFormatText();
  const { settings } = useSettingsStore();
  const { setIsBreadcrumb } = usePageTitle();
  const navigate = useNavigate();
  const { id: teacherId } = useParams<{ id: string }>();
  const breadcrumb = [
    {
      label: formatText('breadcrumb.teachers'),
      to: '/school/teachers-classes',
    },
    {
      label: formatText('teacher-details.title'),
      to: `/school/teachers-classes/teacher-details/${teacherId}`,
    },
    { label: formatText('assigned-classes.title') },
  ];

  useEffect(() => {
    setIsBreadcrumb(breadcrumb);
    return () => {
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const { register, watch } = useForm({
    defaultValues: {
      classSearch: '',
    },
  });

  const classSearch = watch('classSearch');
  const debouncedSearch = useDebounce(classSearch, 500);
  const [classes, setClasses] = useState<ITeacherClass[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!teacherId) return;

      setScreenLoading(true);
      try {
        const res = await getSeeMoreClasses({
          teacher_id: teacherId,
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          ...(debouncedSearch && { search: debouncedSearch }),
        });

        if (res?.status && Array.isArray(res.data)) {
          setClasses(res.data);
          setTotal(res.total || 0);
        } else {
          setClasses([]);
          setTotal(0);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setScreenLoading(false);
      }
    };

    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, debouncedSearch, currentPage]);

  const renderContent = () => {
    if (isScreenLoading) {
      return <AssignedClassesSkeleton />;
    }

    if (classes.length > 0) {
      return (
        <>
          <div className="flex flex-col gap-6 w-full">
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full"
              data-testid="classes-grid"
            >
              {classes.map((cls, index) => (
                <ClassSummaryCard
                  key={cls?.id}
                  name={cls?.name}
                  studentsCount={cls?.total_students}
                  lessonsCount={cls?.total_lessons}
                  colorIndex={index}
                  status={cls?.status}
                  onClick={() =>
                    navigate(
                      `/school/teachers-classes/teacher-details/${teacherId}/class/${cls?.id}`,
                      { state: { className: cls?.name } }
                    )
                  }
                  className="cursor-pointer max-w-full!"
                />
              ))}
            </div>
          </div>
          {total > pageSize && (
            <Pagination
              total={total}
              currentPage={currentPage}
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              className="border-t-0!"
            />
          )}
        </>
      );
    }

    return (
      <EmptyState
        title={formatText('common.no-data-found')}
        description={formatText('assigned-classes.no-classes')}
        className="h-75"
      />
    );
  };

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="assigned-classes-container"
    >
      {/* Search bar */}
      <div className="flex items-center gap-3 ps-2">
        <Input
          name="classSearch"
          register={register}
          placeholder={formatText('assigned-classes.search-placeholder')}
          leftIcon={{
            icon: <IconSearch size={18} className="text-neutral-500" />,
          }}
          className="max-w-70"
          inputClassName="py-3"
          dataTestId="class-search-input"
        />
      </div>
      <h1 className="text-2xl font-semibold text-neutral-900 leading-8">
        {formatText('assigned-classes.title')}
      </h1>

      {/* Classes Grid */}
      {renderContent()}
    </div>
  );
};

export default AssignedClasses;
